import { getAppConfig } from '../config';
import { apiGet } from './client';
import type {
  InstructionSheetConverterRequest,
  InstructionSheetConverterResponse,
  ConverterConfigResponse,
  ConverterConfig,
  InstructionSheetConverterJob,
  ConverterJobListResponse,
  InstructionSheetConverterFileDownloadResponse,
} from '@/lib/types';

// 後方互換性のため、型を再エクスポート
export type {
  InstructionSheetConverterRequest,
  InstructionSheetConverterResponse,
  ConverterConfigResponse,
  ConverterConfig,
  InstructionSheetConverterJob,
  ConverterJobListResponse,
} from '@/lib/types';

// 後方互換性のため、エイリアスをエクスポート
export type FileDownloadResponse = InstructionSheetConverterFileDownloadResponse;

/**
 * 絶対URLを構築するヘルパー関数
 */
export function buildAbsoluteUrl(baseUrl: string, path: string): string {
  if (path.startsWith('http://') || path.startsWith('https://')) {
    return path;
  }

  // baseUrlが相対パス（/から始まる）の場合はoriginを付ける
  if (baseUrl.startsWith('/')) {
    const origin = typeof window !== 'undefined' ? window.location.origin : 'http://localhost:3000';
    const base = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
    const pathWithSlash = path.startsWith('/') ? path : `/${path}`;
    return `${origin}${base}${pathWithSlash}`;
  }

  // baseUrlが絶対URLの場合はそのまま結合
  const base = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
  const pathWithSlash = path.startsWith('/') ? path : `/${path}`;

  return `${base}${pathWithSlash}`;
}

/**
 * 指示書変換設定取得
 */
export async function getConverterConfig(
  accountId: string,
  authHeaders?: Record<string, string>,
): Promise<ConverterConfigResponse> {
  try {
    const url = `/${accountId}/instruction-sheet-converter/config`;
    const response = await apiGet<ConverterConfigResponse>(url, authHeaders);
    return response;
  } catch (error) {
    throw error;
  }
}

/**
 * 指示書変換実行
 */
export async function convertInstructionSheet(
  accountId: string,
  request: InstructionSheetConverterRequest,
  authHeaders?: Record<string, string>,
): Promise<InstructionSheetConverterResponse> {
  const formData = new FormData();

  // 複数ファイルをFormDataに追加
  request.files.forEach((file, index) => {
    formData.append(`files`, file);
  });

  const config = getAppConfig();

  // API Base URLの確認
  if (!config.apiBaseUrl) {
    throw new Error(
      `API Base URLが設定されていません。環境設定を確認してください。環境: ${config.env}`,
    );
  }

  let url: URL;
  try {
    const absoluteUrl = buildAbsoluteUrl(
      config.apiBaseUrl,
      `/${accountId}/instruction-sheet-converter/convert`,
    );
    url = new URL(absoluteUrl);
  } catch (error) {
    throw new Error(`無効なAPI Base URL: ${config.apiBaseUrl}`);
  }

  const headers: Record<string, string> = {
    ...authHeaders,
  };

  const response = await fetch(url.toString(), {
    method: 'POST',
    headers,
    body: formData,
  });

  if (!response.ok) {
    let errorMessage = `指示書変換に失敗しました: ${response.status}`;

    // レスポンスボディからエラーメッセージを取得
    try {
      const responseText = await response.text();

      if (responseText) {
        try {
          const errorData = JSON.parse(responseText);
          if (errorData && errorData.error) {
            errorMessage = errorData.error;
          } else {
            errorMessage = responseText;
          }
        } catch (jsonError) {
          errorMessage = responseText;
        }
      }
    } catch (e) {
      // エラーレスポンスの取得に失敗した場合はデフォルトメッセージを使用
    }

    throw new Error(errorMessage);
  }

  return response.json();
}

// ジョブ一覧取得
export async function getInstructionSheetConverterJobs(
  accountId: string,
  authHeaders: Record<string, string>,
  limit?: number,
  executedBy?: string,
): Promise<InstructionSheetConverterJob[]> {
  try {
    const params = new URLSearchParams();
    if (limit) params.append('limit', limit.toString());
    if (executedBy) params.append('executed_by', executedBy);

    const url = `/${accountId}/instruction-sheet-converter/jobs${params.toString() ? `?${params.toString()}` : ''}`;
    const response = await apiGet<ConverterJobListResponse>(url, authHeaders);
    return response.jobs;
  } catch (error) {
    throw error;
  }
}

// ファイルダウンロードURL取得
export async function getConverterJobFileDownloadUrl(
  accountId: string,
  jobId: string,
  authHeaders: Record<string, string>,
): Promise<FileDownloadResponse> {
  try {
    const url = `/${accountId}/instruction-sheet-converter/jobs/${jobId}/download`;
    const response = await apiGet<FileDownloadResponse>(url, authHeaders);
    return response;
  } catch (error) {
    throw error;
  }
}

// ファイルダウンロード実行
export async function downloadConverterJobFile(
  accountId: string,
  jobId: string,
  authHeaders: Record<string, string>,
): Promise<void> {
  try {
    const downloadInfo = await getConverterJobFileDownloadUrl(accountId, jobId, authHeaders);

    // ブラウザでダウンロードURLを開く
    const link = document.createElement('a');
    link.href = downloadInfo.download_url;
    link.download = downloadInfo.file_name;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } catch (error) {
    throw error;
  }
}

/**
 * CSVコンテンツから直接ファイルをダウンロード
 */
export function downloadCsvFromContent(csvContent: string, filename: string): void {
  try {
    // CSV文字列をBlobに変換（UTF-8 BOM付き）
    const bom = '\uFEFF'; // UTF-8 BOM（Excel対応）
    const csvWithBom = bom + csvContent;
    const blob = new Blob([csvWithBom], {
      type: 'text/csv;charset=utf-8',
    });

    // ダウンロード用のURLを作成
    const url = window.URL.createObjectURL(blob);

    // ダウンロードリンクを作成してクリック
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.style.display = 'none';

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // メモリリークを防ぐためURLを解放
    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.error('CSV ファイルのダウンロードに失敗しました:', error);
    throw new Error('CSV ファイルのダウンロードに失敗しました');
  }
}
